import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { PaginatorModule } from 'primeng/paginator';
import { KeycloakUserManagementResponse } from '../../../models/keycloak-user-management/keycloak-user-management-response.model';
import { SortDirection } from '../../orders/table/sort-direction.enum';
import { QueryParamsService } from '../../../../core/query-params.service';
import { SortKeyForKeycloakUser } from '../../../models/keycloak-user-management/sort-key-for-keycloak-user.enum';

@Component({
  selector: 'app-keycloak-user-management-table',
  standalone: true,
  imports: [CommonModule, PaginatorModule],
  templateUrl: './keycloak-user-management-table.component.html',
  styleUrl: './keycloak-user-management-table.component.scss',
})
export class KeycloakUserManagementTableComponent implements OnChanges {
  private normalizedCustomerNames = new Map<string, string>();

  @Input() users: KeycloakUserManagementResponse[] = [];
  @Input() totalPages = 0;
  @Input() customerNames = new Map<string, string>();
  @Input() emptyMessage = 'Keine Kundennutzer vorhanden.';
  @Output() editClick = new EventEmitter<KeycloakUserManagementResponse>();
  @Output() paginationChangeEvent = new EventEmitter<{ params: HttpParams }>();

  itemsPerPageOptions = [5, 10, 25, 50, 100];
  itemsPerPageShown = this.itemsPerPageOptions[2];
  first = 0;
  currentPage = 0;
  totalRecords = 0;
  sortColumn = '';
  sortDirection: SortDirection = SortDirection.NONE;
  SortDirection = SortDirection;
  SortKeyForKeycloakUser = SortKeyForKeycloakUser;

  columns: { key: string; label: string; allowSort: boolean }[] = [
    { key: SortKeyForKeycloakUser.FIRST_NAME, label: 'Vorname', allowSort: true },
    { key: SortKeyForKeycloakUser.LAST_NAME, label: 'Nachname', allowSort: true },
    { key: SortKeyForKeycloakUser.EMAIL, label: 'E-Mail', allowSort: true },
    { key: SortKeyForKeycloakUser.CUSTOMER_ID, label: 'Kunde', allowSort: true },
  ];

  constructor(private queryService: QueryParamsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const http = this.queryService.getCurrentQueryParams();
    this.updatePaginationParams(http);
    this.updateSortParams(http);
    if (changes['customerNames']) {
      this.normalizedCustomerNames = new Map(
        Array.from(this.customerNames.entries()).map(([id, name]) => [this.normalizeCustomerId(id), name])
      );
    }
    if (changes['users'] && changes['users'].currentValue) {
      this.totalRecords = this.totalPages * this.itemsPerPageShown;
    }
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.currentPage = event.page;
    this.itemsPerPageShown = event.rows;
    let http = this.queryService.getCurrentQueryParams();
    http = http.set('page', this.currentPage).set('size', this.itemsPerPageShown);
    this.paginationChangeEvent.emit({ params: http });
  }

  setSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : this.sortDirection === SortDirection.DESC
            ? SortDirection.NONE
            : SortDirection.ASC;
      if (this.sortDirection === SortDirection.NONE) {
        this.sortColumn = '';
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = SortDirection.ASC;
    }
    this.setSortingParams(this.sortColumn, this.sortDirection);
  }

  setSortingParams(sortKey: string, sortDirection: string): void {
    let http = this.queryService.getCurrentQueryParams();
    http =
      sortDirection !== SortDirection.NONE
        ? http.set('sortKey', sortKey).set('sortDirection', sortDirection)
        : http.delete('sortKey').delete('sortDirection');
    this.paginationChangeEvent.emit({ params: http });
  }

  onEditClick(user: KeycloakUserManagementResponse, event: Event): void {
    event.stopPropagation();
    this.editClick.emit(user);
  }

  getCustomerName(customerId: string): string {
    return (
      this.customerNames.get(customerId) ||
      this.normalizedCustomerNames.get(this.normalizeCustomerId(customerId)) ||
      customerId
    );
  }

  private updatePaginationParams(http: HttpParams): void {
    this.currentPage = Number(http.get('page')) || 0;
    this.itemsPerPageShown = Number(http.get('size')) || this.itemsPerPageShown;
    this.first = this.currentPage * this.itemsPerPageShown;
  }

  private updateSortParams(http: HttpParams): void {
    const sortKeyParam = http.get('sortKey');
    const sortDirectionParam = http.get('sortDirection');
    this.sortColumn = sortKeyParam || '';
    this.sortDirection =
      sortDirectionParam && Object.values(SortDirection).includes(sortDirectionParam as SortDirection)
        ? (sortDirectionParam as SortDirection)
        : SortDirection.NONE;
  }

  private normalizeCustomerId(customerId: string): string {
    return customerId.trim().toLowerCase();
  }
}
